/**
 * Helpers pure planning — Phase 1, incrément 7.
 *
 * Logique testable (sans DOM ni store) pour la fusion événements +
 * tâches en items chronologiques, le split today/upcoming, et la
 * détection des jours du mois qui ont du contenu.
 */
import type { FamilyEvent, FamilyTask } from '@nestor/shared-types';
import { compareIso } from '../../lib/date.js';

/** Sort key d'un événement : préfère `time` quand défini, sinon début de jour. */
function eventSortKey(e: FamilyEvent): string {
  return `${e.date}T${e.time ?? '00:00'}`;
}

/** Sort key d'une tâche : `due` si défini (en fin de jour), sinon date max. */
function taskSortKey(t: FamilyTask): string {
  return `${t.due ?? '9999-99-99'}T23:59`;
}

export type PlanningItem =
  | { type: 'event'; date: string; sort: string; data: FamilyEvent }
  | { type: 'task'; date: string; sort: string; data: FamilyTask };

/**
 * Fusionne événements et tâches en cours en items chronologiques. Les
 * tâches `done` sont exclues (elles vivent dans la section « Terminées »
 * du proto, qui n'apparaît pas dans la vue planning).
 */
export function buildPlanningItems(
  events: readonly FamilyEvent[],
  tasks: readonly FamilyTask[],
): PlanningItem[] {
  const items: PlanningItem[] = [
    ...events.map(
      (e): PlanningItem => ({
        type: 'event',
        date: e.date,
        sort: eventSortKey(e),
        data: e,
      }),
    ),
    ...tasks
      .filter((t) => !t.done)
      .map(
        (t): PlanningItem => ({
          type: 'task',
          date: t.due ?? '9999-99-99',
          sort: taskSortKey(t),
          data: t,
        }),
      ),
  ];
  items.sort((a, b) => compareIso(a.sort, b.sort));
  return items;
}

/** Items dont la date == today (tâches sans `due` exclues automatiquement
 *  car leur date sera `9999-99-99`). */
export function todayItems(items: readonly PlanningItem[], today: string): PlanningItem[] {
  return items.filter((i) => i.date === today);
}

/** Items futurs (date > today). Limite optionnelle. */
export function upcomingItems(
  items: readonly PlanningItem[],
  today: string,
  limit?: number,
): PlanningItem[] {
  const future = items.filter((i) => compareIso(i.date, today) > 0);
  return typeof limit === 'number' ? future.slice(0, limit) : future;
}

/** Set de days-of-month qui ont au moins un item dans le mois donné.
 *  `month1` est 1-12 (pas l'index JS). */
export function daysWithContent(
  items: readonly PlanningItem[],
  year: number,
  month1: number,
): Set<number> {
  const prefix = `${year}-${String(month1).padStart(2, '0')}-`;
  const out = new Set<number>();
  for (const item of items) {
    if (!item.date.startsWith(prefix)) continue;
    const day = Number(item.date.slice(8, 10));
    if (Number.isInteger(day) && day >= 1 && day <= 31) out.add(day);
  }
  return out;
}
